import NoDataAvailable from "components/design/noDataAvailable";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import { useGetFilingDraftDetailsQuery } from "store/apis/filingDraftReview";
import { useGetPreviewUrlQuery } from "store/apis/previewUrl";
import { PdfPreview } from "components/PreviewModal";
import { ModalProps } from "types/utils/modal";
import { ApproveFilingDraft } from "./ApproveFilingDraft";
import { Parse1120Loader } from "./Parse1120Loader";
import { Parsed1120Field } from "./Parsed1120Field";
import { RejectFilingDraft } from "./RejectFilingDraft";
import { Tag } from "components/DesignSystem/Tag/Tag";

export type ReviewParsed1120FieldsType = Record<
  string,
  | string
  | number
  | null
  | { name: string | null; percentage: string | null }[]
  | Record<any, any>
>;

const SectionDescription = ({
  approvalStatus,
  rejectionReason,
  submittedBy,
}: {
  approvalStatus: boolean | null;
  rejectionReason: string | null;
  submittedBy: string | null;
}) => {
  if (approvalStatus === false) {
    return (
      <div className="t-space-y-1">
        <div className="t-text-body-sm t-text-text-60">
          Reason for rejection
        </div>
        <div className="t-text-body-sm t-text-text-30 t-p-3 t-rounded-md t-bg-neutral-0 t-border t-border-solid t-border-neutral-10">
          {rejectionReason}
        </div>
        <div className="t-text-body-sm t-text-text-30 t-italic">
          Rejected by {submittedBy}
        </div>
      </div>
    );
  }

  if (approvalStatus === true) {
    return (
      <div className="t-text-body-sm t-text-text-30 t-italic">
        Approved by {submittedBy}
      </div>
    );
  }

  return (
    <div className="t-text-body-sm t-text-text-30">
      Here's a list of the key fields from your filing draft. Please review them
      carefully before proceeding. You can approve the draft or reject it if any
      corrections are needed.
    </div>
  );
};

const ModalTitle = ({ approvalStatus }: { approvalStatus: boolean | null }) => {
  if (approvalStatus === false) {
    return (
      <div className="t-flex t-items-center t-gap-3">
        Filing Draft
        <Tag tagType="red" rounded>
          Rejected
        </Tag>
      </div>
    );
  }
  if (approvalStatus === true) {
    return (
      <div className="t-flex t-items-center t-gap-3">
        Final Filing Draft
        <Tag tagType="green" rounded>
          Approved
        </Tag>
      </div>
    );
  }
  return <>Review Filing Draft</>;
};

export const ReviewDraftFiling = ({
  isOpen,
  close,
  draftId,
  groupId,
  isSubmitted,
  approvalStatus,
  rejectionReason,
  submittedBy,
}: ModalProps & {
  draftId: string;
  groupId: string;
  isSubmitted: boolean;
  approvalStatus: boolean | null;
  rejectionReason: string | null;
  submittedBy: string | null;
}) => {
  const discardDraft = useModal();
  const sendConfirmation = useModal();
  const { data, isLoading, isSuccess } = useGetFilingDraftDetailsQuery(
    { draftId, groupId },
    { skip: !draftId }
  );
  const {
    data: fileData,
    isLoading: fileDataLoading,
    isSuccess: fileDataSuccess,
  } = useGetPreviewUrlQuery(
    {
      groupId: groupId,
      fileId: data?.file_id,
    },
    {
      skip: !data?.file_id || !groupId,
      refetchOnMountOrArgChange: true,
    }
  );

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="xxl">
          <Modal.Header>
            <Modal.Title>
              <ModalTitle approvalStatus={approvalStatus} />
            </Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="!t-p-0 t-h-full">
            <Async.Root
              {...{
                isLoading: isLoading || fileDataLoading,
                isEmpty: !data,
                isSuccess: isSuccess && fileDataSuccess,
              }}
              customLoader={<Parse1120Loader />}
            >
              <Async.Empty>
                <NoDataAvailable />
              </Async.Empty>
              <Async.Success>
                <div className="t-flex t-gap-1 sm:t-flex-col">
                  <div className="t-w-96 t-h-[500px] t-overflow-auto t-border t-border-solid t-border-l-0 t-border-t-0 t-border-b-0 t-border-neutral-10 sm:t-h-auto sm:t-w-full sm:t-border-r-0">
                    <div className="t-border t-border-solid t-border-l-0 t-border-t-0 t-border-r-0 t-border-neutral-10 t-px-8 t-py-3">
                      <SectionDescription
                        approvalStatus={approvalStatus}
                        rejectionReason={rejectionReason}
                        submittedBy={submittedBy}
                      />
                    </div>
                    {data?.parsed_fields?.map((field) => (
                      <Parsed1120Field
                        key={field.key}
                        field={field}
                        isEditable={false}
                        isEdited={field.is_edited}
                      />
                    ))}
                  </div>
                  <div className="t-flex-1 t-h-[500px] sm:t-h-[60vh] sm:t-flex-none">
                    <PdfPreview
                      preview_url={fileData?.preview_url || ""}
                      name="Document Preview"
                    />
                  </div>
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          {data && (
            <>
              {isSubmitted ? (
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Close</Button>
                  </Modal.RawClose>
                </Modal.FooterButtonGroup>
              ) : (
                <Modal.FooterButtonGroup>
                  <Button
                    onClick={discardDraft.open}
                    customType="danger_outlined"
                    size="small"
                  >
                    Reject draft
                  </Button>
                  <Button
                    customType="primary"
                    disabled={isLoading}
                    onClick={sendConfirmation.open}
                    size="small"
                  >
                    Approve draft
                  </Button>
                </Modal.FooterButtonGroup>
              )}
            </>
          )}
        </Modal.Content>
      </Modal.Root>
      <RejectFilingDraft
        isOpen={discardDraft.isOpen}
        close={discardDraft.close}
        groupId={groupId}
        draftId={draftId}
        closeParent={close}
      />
      <ApproveFilingDraft
        isOpen={sendConfirmation.isOpen}
        close={sendConfirmation.close}
        groupId={groupId}
        draftId={draftId}
        closeParent={close}
      />
    </>
  );
};
